import { useEffect, useState } from 'react';
import { api } from '../api';
import { formatEuros, localDate, shortDate } from '../format';
import type { DepositLine, DepositOverviewRow, DepositSettlementDetail } from '../types';

const METHOD_LABELS: Record<string, string> = {
  VIREMENT: 'Virement', ESPECES: 'Espèces', CHEQUE: 'Chèque', AUTRE: 'Autre',
};

/**
 * Le chiffrage d'une retenue se fait ici, après l'état des lieux de sortie —
 * pas sur place. L'état des lieux dit seulement « imputable ou non » ; cet
 * écran reprend ces éléments et laisse décider du montant à tête reposée,
 * avec la grille de vétusté comme aide au calcul, jamais une valeur imposée.
 */
function LineEditor({ line, disabled, onSave, onRemove }: {
  line: DepositLine;
  disabled: boolean;
  onSave: (input: { description: string; replacementCostCents: number | null; itemAgeYears: number | null; amountCents: number }) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const [description, setDescription] = useState(line.description);
  const [replacementCostCents, setReplacementCostCents] = useState<number | null>(line.replacementCostCents);
  const [itemAgeYears, setItemAgeYears] = useState<number | null>(line.itemAgeYears);
  const [amountCents, setAmountCents] = useState(line.amountCents);
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await onSave({ description, replacementCostCents, itemAgeYears, amountCents });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="record-card deposit-line">
      <div className="record-heading">
        <strong>{line.roomLabel} · {line.itemLabel}</strong>
        {!disabled && <button className="icon-button danger" aria-label="Retirer" title="Retirer" onClick={() => void onRemove()}>×</button>}
      </div>
      <label className="span-2">Description
        <textarea disabled={disabled} rows={2} value={description} onChange={(event) => setDescription(event.target.value)} onBlur={() => void save()} />
      </label>
      <div className="wear-scale">
        <label>Coût de remplacement <span>
          <input
            disabled={disabled}
            type="number" min="0" step="1" placeholder="devis"
            value={replacementCostCents === null ? '' : replacementCostCents / 100}
            onChange={(event) => setReplacementCostCents(event.target.value === '' ? null : Math.round(Number(event.target.value) * 100))}
            onBlur={() => void save()}
          /> €
        </span></label>
        <label>Âge estimé <span>
          <input
            disabled={disabled}
            type="number" min="0" step="1" placeholder="années"
            value={itemAgeYears ?? ''}
            onChange={(event) => setItemAgeYears(event.target.value === '' ? null : Math.round(Number(event.target.value)))}
            onBlur={() => void save()}
          /> ans
        </span></label>
        <p className="wear-explanation">{line.wearAllowance.explanation}</p>
        {line.wearAllowance.suggestedCents !== null && (
          <button
            type="button"
            className="text-button"
            disabled={disabled || busy}
            onClick={() => { setAmountCents(line.wearAllowance.suggestedCents!); void save(); }}
          >
            Reprendre {formatEuros(line.wearAllowance.suggestedCents)} comme retenue
          </button>
        )}
      </div>
      <label>Retenue <span>
        <input
          disabled={disabled}
          type="number" min="0" step="1"
          value={amountCents / 100 || ''}
          onChange={(event) => setAmountCents(Math.round(Number(event.target.value) * 100) || 0)}
          onBlur={() => void save()}
        /> €
      </span></label>
    </article>
  );
}

function SettlementDetail({ leaseId, onClose, onChanged }: { leaseId: string; onClose: () => void; onChanged: () => Promise<void> }) {
  const [detail, setDetail] = useState<DepositSettlementDetail | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [newRoom, setNewRoom] = useState('');
  const [newItem, setNewItem] = useState('');
  const [refundOn, setRefundOn] = useState(localDate());
  const [refundMethod, setRefundMethod] = useState('VIREMENT');

  async function load(): Promise<void> {
    try {
      const result = await api<DepositSettlementDetail>(`/api/admin/deposits/${leaseId}`);
      setDetail(result);
      setNotes(result.notes);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, [leaseId]);

  const settled = Boolean(detail?.settledAt);

  async function saveLine(lineId: string, input: { description: string; replacementCostCents: number | null; itemAgeYears: number | null; amountCents: number }): Promise<void> {
    try {
      await api(`/api/admin/deposits/lines/${lineId}`, { method: 'PATCH', body: JSON.stringify(input) });
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    }
  }

  async function removeLine(lineId: string): Promise<void> {
    if (!window.confirm('Retirer cette ligne ?')) return;
    try {
      await api(`/api/admin/deposits/lines/${lineId}`, { method: 'DELETE' });
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Suppression impossible.');
    }
  }

  async function addLine(): Promise<void> {
    if (!detail || !newRoom.trim() || !newItem.trim()) return;
    try {
      await api(`/api/admin/deposits/${detail.id}/lines`, {
        method: 'POST',
        body: JSON.stringify({ roomLabel: newRoom.trim(), itemLabel: newItem.trim() }),
      });
      setNewRoom('');
      setNewItem('');
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Ajout impossible.');
    }
  }

  async function finalize(): Promise<void> {
    if (!detail) return;
    if (!window.confirm(`Solder la caution : rembourser ${formatEuros(detail.refundCents)} et retenir ${formatEuros(detail.retainedCents)} ?`)) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/deposits/${detail.id}/finalize`, { method: 'POST', body: JSON.stringify({ notes }) });
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Solde impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function reopen(): Promise<void> {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/deposits/${detail.id}/reopen`, { method: 'POST' });
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Réouverture impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function disburse(): Promise<void> {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/deposits/${detail.id}/disburse`, {
        method: 'POST', body: JSON.stringify({ paidOn: refundOn, method: refundMethod }),
      });
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!detail) return <div className="modal-backdrop" onClick={onClose}><div className="modal-card" onClick={(event) => event.stopPropagation()}><p className="muted">Chargement…</p></div></div>;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div><p className="eyebrow">Caution</p><h2>{detail.roomName} · {detail.tenantName}</h2></div>
          <button type="button" className="icon-button" aria-label="Fermer" onClick={onClose}>×</button>
        </div>

        <dl className="detail-grid balance-grid">
          <div><dt>Dépôt détenu</dt><dd>{formatEuros(detail.depositCents)}</dd></div>
          <div><dt>Total retenu</dt><dd>{formatEuros(detail.retainedCents)}</dd></div>
          <div><dt>À rembourser</dt><dd>{formatEuros(detail.refundCents)}</dd></div>
        </dl>

        {settled && (
          <div className="deposit-note">
            <strong>Caution soldée</strong>
            <span>Décision figée. « Rouvrir » si une correction est nécessaire.</span>
          </div>
        )}

        {settled && detail.refundCents > 0 && (
          detail.refundPaidAt ? (
            <div className="deposit-note">
              <strong>Remboursement effectué</strong>
              <span>{formatEuros(detail.refundCents)} rendus le {shortDate(detail.refundPaidAt)} par {METHOD_LABELS[detail.refundMethod] ?? detail.refundMethod}.</span>
            </div>
          ) : (
            <section className="modal-section">
              <p className="eyebrow">Décaissement</p>
              <p className="muted">
                {formatEuros(detail.refundCents)} restent à rendre au locataire. Une fois le virement ou le
                chèque réellement parti, enregistrez-le ici : le dépôt sort alors des « détenus » du tableau
                de bord.
              </p>
              <div className="form-grid">
                <label>Remboursé le
                  <input type="date" value={refundOn} onChange={(event) => setRefundOn(event.target.value)} />
                </label>
                <label>Mode
                  <select value={refundMethod} onChange={(event) => setRefundMethod(event.target.value)}>
                    {Object.entries(METHOD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
              </div>
              <div className="record-actions">
                <button type="button" className="button primary compact" disabled={busy} onClick={() => void disburse()}>
                  {busy ? 'Enregistrement…' : 'Enregistrer le remboursement'}
                </button>
              </div>
            </section>
          )
        )}

        <section className="modal-section">
          <p className="eyebrow">Éléments imputables</p>
          {detail.lines.length === 0 && <p className="muted">Aucun élément retenu.</p>}
          <div className="record-list">
            {detail.lines.map((line) => (
              <LineEditor
                key={line.id}
                line={line}
                disabled={settled}
                onSave={(input) => saveLine(line.id, input)}
                onRemove={() => removeLine(line.id)}
              />
            ))}
          </div>
          {!settled && (
            <div className="inline-form">
              <label>Chambre / lieu<input value={newRoom} onChange={(event) => setNewRoom(event.target.value)} placeholder="Chambre Beige" /></label>
              <label>Élément<input value={newItem} onChange={(event) => setNewItem(event.target.value)} placeholder="Matelas" /></label>
              <button type="button" className="button secondary compact" onClick={() => void addLine()}>+ Ajouter une ligne</button>
            </div>
          )}
        </section>

        <section className="modal-section">
          <label className="span-2">Notes (facultatif)
            <textarea disabled={settled} rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </label>
          {error && <div className="inline-message error" role="alert">{error}</div>}
          <div className="modal-actions">
            {settled
              ? <button type="button" className="button secondary compact" disabled={busy} onClick={() => void reopen()}>Rouvrir</button>
              : <button type="button" className="button primary compact" disabled={busy} onClick={() => void finalize()}>Solder la caution</button>}
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * Encaissement du dépôt : montant dû, reçu, date, mode. Saisie et correction
 * directement dans le tableau, sans passer par le formulaire de versement
 * générique — la caution a son propre cycle, de l'encaissement à la restitution.
 */
function DepositReceipt({ row, onChanged }: { row: DepositOverviewRow; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [amountEuros, setAmountEuros] = useState((row.depositCents / 100).toFixed(2));
  const [receivedOn, setReceivedOn] = useState(localDate());
  const [method, setMethod] = useState('VIREMENT');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function startEdit(): void {
    setAmountEuros(((row.receivedCents || row.depositCents) / 100).toFixed(2));
    setReceivedOn(row.receivedOn ?? localDate());
    setMethod(row.method ?? 'VIREMENT');
    setError('');
    setEditing(true);
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const amountCents = Math.round(Number(amountEuros.replace(',', '.')) * 100);
      if (row.paymentId) {
        await api(`/api/admin/deposits/payments/${row.paymentId}`, {
          method: 'PATCH', body: JSON.stringify({ amountCents, receivedOn, method }),
        });
      } else {
        await api(`/api/admin/deposits/${row.leaseId}/receipt`, {
          method: 'POST', body: JSON.stringify({ amountCents, receivedOn, method }),
        });
      }
      setEditing(false);
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!row.paymentId || !window.confirm('Retirer cet encaissement ?')) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/deposits/payments/${row.paymentId}`, { method: 'DELETE' });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Suppression impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!row.editable) {
    return (
      <p className="record-line muted">
        {formatEuros(row.receivedCents)} reçu sur {formatEuros(row.depositCents)} (plusieurs versements, voir Finances)
      </p>
    );
  }

  if (editing) {
    return (
      <div className="deposit-receipt-form" onClick={(event) => event.stopPropagation()}>
        <label>Montant (€)<input inputMode="decimal" value={amountEuros} onChange={(event) => setAmountEuros(event.target.value)} /></label>
        <label>Reçu le<input type="date" value={receivedOn} onChange={(event) => setReceivedOn(event.target.value)} /></label>
        <label>Mode
          <select value={method} onChange={(event) => setMethod(event.target.value)}>
            {Object.entries(METHOD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <div className="record-actions">
          <button type="button" className="text-button" onClick={() => setEditing(false)}>Annuler</button>
          <button type="button" className="button primary compact" disabled={busy} onClick={() => void save()}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
        {error && <div className="inline-message error" role="alert">{error}</div>}
      </div>
    );
  }

  return (
    <p className="record-line">
      <span className={row.receivedCents >= row.depositCents ? 'status signed' : 'status draft'}>
        {row.receivedCents > 0
          ? `${formatEuros(row.receivedCents)} reçu${row.receivedOn ? ` le ${shortDate(row.receivedOn)}` : ''}${row.method ? ` · ${METHOD_LABELS[row.method] ?? row.method}` : ''}`
          : `${formatEuros(row.depositCents)} dus, rien reçu`}
      </span>
      <span className="deposit-receipt-actions">
        <button type="button" className="text-button" onClick={(event) => { event.stopPropagation(); startEdit(); }}>
          {row.receivedCents > 0 ? 'Modifier' : 'Enregistrer l’encaissement'}
        </button>
        {row.paymentId && (
          <button type="button" className="text-button" onClick={(event) => { event.stopPropagation(); void remove(); }}>Retirer</button>
        )}
      </span>
    </p>
  );
}

export function DepositsPanel() {
  const [deposits, setDeposits] = useState<DepositOverviewRow[] | null>(null);
  const [error, setError] = useState('');
  const [openLeaseId, setOpenLeaseId] = useState('');

  async function load(): Promise<void> {
    try {
      const result = await api<{ deposits: DepositOverviewRow[] }>('/api/admin/deposits');
      setDeposits(result.deposits);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, []);

  const active = deposits?.filter((row) => row.active) ?? [];
  const ended = deposits?.filter((row) => !row.active) ?? [];

  return (
    <section className="panel">
      {openLeaseId && (
        <SettlementDetail leaseId={openLeaseId} onClose={() => setOpenLeaseId('')} onChanged={load} />
      )}
      <div className="section-heading">
        <div><p className="eyebrow">Caution</p><h2>Dépôts de garantie</h2></div>
        <span className="count-badge">{deposits?.length ?? 0}</span>
      </div>
      <p className="section-intro">
        Le cycle complet, chambre par chambre : encaissement du dépôt à l'entrée, puis retenue et restitution
        à la sortie. Le montant retenu se chiffre après l'état des lieux de sortie, pas sur place.
      </p>

      {error && <div className="inline-message error" role="alert">{error}</div>}
      {!deposits && !error && <p className="muted">Chargement…</p>}
      {deposits?.length === 0 && <div className="empty-state">Aucun bail ne porte de dépôt de garantie.</div>}

      {active.length > 0 && (
        <>
          <h3 className="subhead">Baux en cours ({active.length})</h3>
          <div className="record-list">
            {active.map((row) => (
              <article className="record-card" key={row.leaseId}>
                <div className="record-heading">
                  <strong>{row.roomName} · {row.tenantName}</strong>
                </div>
                <DepositReceipt row={row} onChanged={load} />
              </article>
            ))}
          </div>
        </>
      )}

      {ended.length > 0 && (
        <>
          <h3 className="subhead">Baux terminés ({ended.length})</h3>
          <div className="record-list">
            {ended.map((row) => (
              <article className="record-card" key={row.leaseId}>
                <div className="record-heading">
                  <strong>{row.roomName} · {row.tenantName}</strong>
                  <span className="record-code">Départ le {row.actualEnd ? shortDate(row.actualEnd) : '—'}</span>
                </div>
                <DepositReceipt row={row} onChanged={load} />
                {row.settlement && (
                  <button
                    type="button"
                    className={`anomaly-row clickable ${row.heldCents === 0 ? 'resolved' : ''}`}
                    onClick={() => setOpenLeaseId(row.leaseId)}
                  >
                    <div className="anomaly-main">
                      <span className={row.heldCents === 0 ? 'status signed' : 'status draft'}>
                        {!row.settlement.settledAt
                          ? `À solder · ${formatEuros(row.settlement.retainedCents)} retenu pour l’instant`
                          : row.settlement.refundCents === 0
                            ? `Soldée le ${shortDate(row.settlement.settledAt)} · retenue intégrale, rien à rendre`
                            : row.settlement.refundPaidAt
                              ? `Remboursée le ${shortDate(row.settlement.refundPaidAt)} · ${formatEuros(row.settlement.refundCents)}`
                              : `Soldée le ${shortDate(row.settlement.settledAt)} · ${formatEuros(row.settlement.refundCents)} restent à rembourser`}
                      </span>
                    </div>
                    <span className="anomaly-chevron" aria-hidden="true">›</span>
                  </button>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
